import { Card, Col, Row } from 'antd';
import {
  AlertOutlined, BankOutlined, DollarOutlined, FallOutlined, UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { MaskedAmount } from '@/shared/components/masked-amount';
import type { DashboardSummary } from '@/api/dashboard';
import {
  DashboardHeader, KpiGrid, PKR, RecentNoticesCard, SignalRow,
  type KpiSpec,
} from './shared';

/**
 * Accountant dashboard — money in, money out, who hasn't paid. No HR or
 * academic noise. Quick links to defaulters, daybook, trial balance.
 */
export function AccountantDashboard({ data }: { data: DashboardSummary }) {
  const kpis: KpiSpec[] = [
    {
      key: 'fees-today',
      label: 'Fees collected today',
      value: PKR(Number(data.fees.collectedToday)),
      // Hint sits inside the same RevealGroup as the main value, so the
      // single eye toggle on the card unmasks both numbers in lock-step.
      hint: <><MaskedAmount value={PKR(Number(data.fees.collectedThisMonth))} maskWidth={5} /> this month</>,
      sensitive: true,
      icon: <DollarOutlined />,
      tone: 'success',
    },
    {
      key: 'outstanding',
      label: 'Outstanding dues',
      value: PKR(Number(data.fees.outstanding)),
      hint: `${data.fees.defaulterCount.toLocaleString()} defaulters`,
      sensitive: true,
      icon: <AlertOutlined />,
      tone: 'warning',
    },
    {
      key: 'defaulters',
      label: 'Defaulter count',
      value: data.fees.defaulterCount.toLocaleString(),
      hint: 'Students with ≥1 unpaid month',
      icon: <UserOutlined />,
      tone: 'info',
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <DashboardHeader role={data.role} asOf={data.asOf} title="Finance overview" />
      <KpiGrid kpis={kpis} />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Quick actions" size="small">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
              <li><Link to="/fee/defaulters">Defaulter list</Link></li>
              <li><Link to="/fee/collect">Collect a payment</Link></li>
              <li><Link to="/finance/daybook">Today's daybook</Link></li>
              <li><Link to="/finance/expenses">Pending expense approvals</Link></li>
              <li><Link to="/finance/trial-balance">Trial balance</Link></li>
              <li><Link to="/reports/fee-collection">Fee collection report</Link></li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="At a glance" size="small">
            <SignalRow icon={<BankOutlined />} label="Active students" value={data.students.active.toLocaleString()} />
            <SignalRow icon={<DollarOutlined />} label="Total on roll" value={data.students.total.toLocaleString()} />
            <SignalRow icon={<FallOutlined />} label="Low-stock items" value={data.inventory.lowStockCount.toString()} />
          </Card>
        </Col>
      </Row>

      <RecentNoticesCard notices={data.recentNotices} />
    </div>
  );
}
